/**
 * Node visual "Alterar Fase da Tarefa" (task_change_phase)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Exibe a origem da tarefa (gatilho ou variável) e a fase de destino,
 * seguindo o mesmo padrão visual do TaskCreateNode (amber-500).
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { ArrowRightLeft } from 'lucide-react';
import { FlowNodeData, TaskChangePhaseConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

export function TaskChangePhaseNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as TaskChangePhaseConfig | undefined;

  // Configurado: precisa de origem da tarefa + fase de destino válida
  const isConfigured = !!(
    config?.phase_id &&
    config?.task_source &&
    (config.task_source === 'from_trigger' ||
      (config.task_source === 'from_variable' && config.task_variable))
  );

  const sourceLabel =
    config?.task_source === 'from_variable'
      ? `{{${config.task_variable || 'variavel'}}}`
      : 'Tarefa do gatilho';

  return (
    <>
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />

      <div
        className={`
          min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
          ${selected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-amber-400'}
          transition-all duration-200
        `}
      >
        <div className="flex items-center gap-2 p-3 border-b border-border bg-amber-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-amber-500">
            <ArrowRightLeft className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm truncate">
            {config?.nickname || 'Alterar Fase da Tarefa'}
          </span>
        </div>

        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Tarefa:</span>
                <span className="font-medium truncate" title={sourceLabel}>
                  {sourceLabel}
                </span>
              </div>

              {config?.category_name && (
                <div className="flex items-center gap-1.5">
                  {config.category_color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block"
                      style={{ backgroundColor: config.category_color }}
                    />
                  )}
                  <span className="text-muted-foreground">Categoria:</span>
                  <span className="font-medium truncate">{config.category_name}</span>
                </div>
              )}

              <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                {config?.phase_color && (
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block"
                    style={{ backgroundColor: config.phase_color }}
                  />
                )}
                <span className="text-muted-foreground">Mover para:</span>
                <span className="font-medium truncate" title={config?.phase_name}>
                  {config?.phase_name || '—'}
                </span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar
            </p>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-amber-500 !border-2 !border-white"
      />
    </>
  );
}
